// pages/SAPGlCompare.jsx — compare G/L company-code settings (SKB1) across company codes.
// Live: GET /api/sap-procmap/gl?land=&gl=  (one G/L or a prefix ending in *).
// Values that differ from the majority of company codes for the same G/L are highlighted,
// e.g. tax category set in 1800 but empty elsewhere.
import { useMemo, useState } from 'react';
import { api } from '../context/AuthContext';

const F = {
  MWSKZ: { en: 'Tax category', id: 'Kategori pajak', vals: { '': { en: 'not tax-relevant', id: 'tidak relevan pajak' }, '*': { en: 'all tax types', id: 'semua jenis pajak' }, '-': { en: 'input tax only', id: 'hanya pajak masukan' }, '+': { en: 'output tax only', id: 'hanya pajak keluaran' } } },
  XMWNO: { en: 'Posting w/o tax allowed', id: 'Boleh posting tanpa pajak' },
  FSTAG: { en: 'Field status group', id: 'Field status group' },
  XOPVW: { en: 'Open item mgmt', id: 'Open item' },
  XKRES: { en: 'Line items', id: 'Line item' },
  WAERS: { en: 'Currency', id: 'Mata uang' },
  MITKZ: { en: 'Recon. account', id: 'Rekonsiliasi' },
  XINTB: { en: 'Auto posting only', id: 'Hanya posting otomatis' },
  ZUAWA: { en: 'Sort key', id: 'Sort key' },
  XSPEB: { en: 'Blocked', id: 'Diblokir' },
  XLOEB: { en: 'Marked for deletion', id: 'Ditandai hapus' },
};
const T = {
  en: { title: 'Compare G/L settings across company codes', ph: 'G/L e.g. 53101028 or prefix 531010*', go: 'Compare', busy: 'Reading SAP…', onlyDiff: 'Only G/Ls with differences',
    sum: (n, d) => `${n} G/L · ${d} with differences`, hint: 'Highlighted = differs from most company codes for that G/L. A difference is not always wrong (a company may need it), but it is the first thing to check when a posting behaves differently in one company.', none: 'No G/L found.', co: 'Company', bs: 'Balance sheet', pl: 'P&L', majority: 'Most companies' },
  id: { title: 'Bandingkan setting G/L antar company code', ph: 'G/L mis. 53101028 atau prefix 531010*', go: 'Bandingkan', busy: 'Membaca SAP…', onlyDiff: 'Hanya G/L yang berbeda',
    sum: (n, d) => `${n} G/L · ${d} berbeda`, hint: 'Disorot = berbeda dari mayoritas company code untuk G/L tsb. Beda tidak selalu salah (bisa memang kebutuhan company), tapi ini hal pertama yang dicek saat posting di satu company berperilaku lain.', none: 'G/L tidak ditemukan.', co: 'Company', bs: 'Neraca', pl: 'Laba rugi', majority: 'Mayoritas' },
};
const label = (f, v, L) => {
  const d = F[f] && F[f].vals && F[f].vals[v];
  if (d) return `${v || '∅'} · ${d[L]}`;
  return v === '' ? '—' : v === 'X' ? '✓' : v;
};

export default function SAPGlCompare({ landKey, lang }) {
  const L = lang === 'id' ? 'id' : 'en';
  const t = T[L];
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [onlyDiff, setOnlyDiff] = useState(true);

  async function run(e) {
    if (e) e.preventDefault();
    if (!q.trim()) return;
    setBusy(true); setErr('');
    try {
      const { data: d } = await api.get('/api/sap-procmap/gl', { params: { land: landKey, gl: q.trim() }, timeout: 180000 });
      setData(d);
    } catch (e2) { setErr((e2.response && e2.response.data && e2.response.data.error) || e2.message); }
    finally { setBusy(false); }
  }

  const gls = useMemo(() => {
    if (!data) return [];
    return data.gls.filter(g => !onlyDiff || data.gls.length === 1 || g.cos.some(c => c.diff.length));
  }, [data, onlyDiff]);

  return (
    <div className="flex flex-col gap-2 border border-slate-200 rounded-lg p-3">
      <form onSubmit={run} className="flex flex-wrap items-center gap-2">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mr-auto">{t.title}</div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t.ph} className="border border-slate-200 rounded-md px-2 py-1 text-[12.5px] w-[240px] font-mono" />
        <button type="submit" disabled={busy || !q.trim()} className="px-2.5 py-1 rounded-md text-[12px] font-semibold border bg-brand-600 text-white border-brand-600 disabled:opacity-60">
          {busy ? t.busy : t.go}
        </button>
      </form>
      {err && <div className="text-[12px] text-rose-600">{err}</div>}
      {data && (
        <div className="flex flex-wrap items-center gap-2 text-[11.5px] text-slate-500">
          <span>{t.sum(data.total, data.withDiff)}</span>
          {data.gls.length > 1 && (
            <label className="inline-flex items-center gap-1 ml-auto cursor-pointer">
              <input type="checkbox" checked={onlyDiff} onChange={e => setOnlyDiff(e.target.checked)} /> {t.onlyDiff}
            </label>
          )}
        </div>
      )}
      {data && !gls.length && <div className="text-[12.5px] text-slate-500 py-2">{t.none}</div>}
      <div className="flex flex-col gap-3 max-h-[560px] overflow-y-auto">
        {gls.map(g => {
          const cols = data.fields.filter(f => g.cos.some(c => c.diff.includes(f)) || ['MWSKZ', 'XMWNO', 'FSTAG', 'XOPVW'].includes(f));
          return (
            <div key={g.gl} className="flex flex-col gap-1">
              <div className="text-[12.5px]"><b className="font-mono text-slate-800">{g.gl}</b> <span className="text-slate-700">{g.text}</span>
                <span className="text-[11px] text-slate-400"> · {g.ktopl} · {g.bs ? t.bs : t.pl} · {g.group}</span></div>
              <div className="overflow-x-auto border border-slate-200 rounded-lg">
                <table className="w-full text-[12px] border-collapse">
                  <thead><tr className="bg-slate-50 text-slate-500 text-[11px]">
                    <th className="px-2 py-1 text-left font-semibold">{t.co}</th>
                    {cols.map(f => <th key={f} className="px-2 py-1 text-left font-semibold whitespace-nowrap" title={f}>{F[f][L]}</th>)}
                  </tr></thead>
                  <tbody>
                    {g.cos.map(c => (
                      <tr key={c.bukrs} className="border-t border-slate-100">
                        <td className="px-2 py-1 whitespace-nowrap"><b className="font-mono">{c.bukrs}</b> <span className="text-[11px] text-slate-500">{data.names[c.bukrs] || ''}</span></td>
                        {cols.map(f => (
                          <td key={f} className={`px-2 py-1 whitespace-nowrap ${c.diff.includes(f) ? 'bg-amber-100 text-amber-900 font-semibold' : 'text-slate-600'}`}>{label(f, c.v[f], L)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11.5px] text-slate-400 m-0">{t.hint}</p>
    </div>
  );
}
